import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Polygon, Tooltip, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Plus, Trash2, Pencil, Undo2, Check, X, Eye, EyeOff } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CONAKRY_CENTER = [9.57, -13.65];
const PALETTE = ["#E32636", "#1B7A43", "#1D6FB8", "#B8621D", "#7A1BB8", "#0E8A8A"];

const vertexIcon = (color) => L.divIcon({
  className: "",
  html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

function DrawClickHandler({ onAdd }) {
  useMapEvents({
    click(e) {
      onAdd([+e.latlng.lat.toFixed(6), +e.latlng.lng.toFixed(6)]);
    },
  });
  return null;
}

export default function DeliveryZonesPanel() {
  const { t } = useTranslation();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  // Mode dessin : { points: [[lat,lng]...], editingId: string|null }
  const [draft, setDraft] = useState(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState(PALETTE[0]);
  const [saving, setSaving] = useState(false);

  const fetchZones = () => {
    apiClient.get("/admin/delivery-zones")
      .then(({ data }) => setZones(data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(fetchZones, []);

  const startDraw = () => {
    setDraft({ points: [], editingId: null });
    setName("");
    setColor(PALETTE[zones.length % PALETTE.length]);
  };

  const startEdit = (z) => {
    setDraft({ points: [...z.polygon], editingId: z.zone_id });
    setName(z.name);
    setColor(z.color || PALETTE[0]);
  };

  const cancelDraw = () => setDraft(null);

  const undoPoint = () => setDraft((d) => ({ ...d, points: d.points.slice(0, -1) }));

  const moveVertex = (idx, lat, lng) =>
    setDraft((d) => ({ ...d, points: d.points.map((p, i) => (i === idx ? [lat, lng] : p)) }));

  const saveDraft = async () => {
    if (!name.trim()) { toast.error(t("admin.zoneNameRequired")); return; }
    if (draft.points.length < 3) { toast.error(t("admin.zone3pts")); return; }
    setSaving(true);
    try {
      if (draft.editingId) {
        await apiClient.put(`/admin/delivery-zones/${draft.editingId}`, { name: name.trim(), color, polygon: draft.points });
        toast.success(t("admin.zoneUpdated"));
      } else {
        await apiClient.post("/admin/delivery-zones", { name: name.trim(), color, polygon: draft.points });
        toast.success(t("admin.zoneCreated"));
      }
      setDraft(null);
      fetchZones();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (z) => {
    try {
      await apiClient.put(`/admin/delivery-zones/${z.zone_id}`, { active: !z.active });
      setZones((zs) => zs.map((x) => (x.zone_id === z.zone_id ? { ...x, active: !z.active } : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteZone = async (z) => {
    if (!window.confirm(`Supprimer la zone « ${z.name} » ? Cette action est définitive.`)) return;
    try {
      await apiClient.delete(`/admin/delivery-zones/${z.zone_id}`);
      setZones((zs) => zs.filter((x) => x.zone_id !== z.zone_id));
      toast.success(t("admin.zoneDeleted"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Zones affichées sur la carte (la zone en cours d'édition est masquée)
  const displayedZones = useMemo(
    () => zones.filter((z) => !(draft?.editingId && z.zone_id === draft.editingId)),
    [zones, draft]
  );

  return (
    <div data-testid="delivery-zones-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-xl font-semibold flex items-center gap-2">
            <MapPin className="w-5 h-5 text-brand-red" /> Zones de livraison
          </h3>
          <p className="text-sm text-mute mt-1">
            Dessinez les zones où NelDaari livre. Sans zone active, la livraison est ouverte partout.
            Au checkout, la position GPS du client doit être dans une zone active.
          </p>
        </div>
        {!draft && (
          <button onClick={startDraw} data-testid="zone-new-btn"
            className="inline-flex items-center gap-2 rounded-full bg-brand-red text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#C81F2D]">
            <Plus className="w-4 h-4" /> Nouvelle zone
          </button>
        )}
      </div>

      {draft && (
        <div className="bg-elevated border border-sand rounded-2xl p-4 mb-4" data-testid="zone-draw-toolbar">
          <div className="flex flex-wrap items-center gap-3">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              placeholder={t("admin.zoneNamePh")}
              className="flex-1 min-w-[200px] bg-white border border-sand rounded-xl px-4 py-2.5 text-sm outline-none"
              data-testid="zone-name-input"
            />
            <div className="flex items-center gap-1.5">
              {PALETTE.map((c) => (
                <button key={c} onClick={() => setColor(c)} aria-label={`Couleur ${c}`}
                  className={`w-7 h-7 rounded-full border-2 ${color === c ? "border-ink scale-110" : "border-white"}`}
                  style={{ backgroundColor: c }} data-testid={`zone-color-${c.slice(1)}`} />
              ))}
            </div>
            <button onClick={undoPoint} disabled={draft.points.length === 0} data-testid="zone-undo-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-sand bg-white px-4 py-2 text-sm font-medium disabled:opacity-40">
              <Undo2 className="w-4 h-4" /> Annuler le point
            </button>
            <button onClick={cancelDraw} data-testid="zone-cancel-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-sand bg-white px-4 py-2 text-sm font-medium">
              <X className="w-4 h-4" /> Abandonner
            </button>
            <button onClick={saveDraft} disabled={saving || draft.points.length < 3 || !name.trim()} data-testid="zone-save-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-green text-white px-5 py-2 text-sm font-semibold disabled:opacity-50">
              <Check className="w-4 h-4" /> {draft.editingId ? t("admin.zoneUpdate") : t("admin.zoneSave")} ({draft.points.length} pts)
            </button>
          </div>
          <p className="text-xs text-mute mt-2">
            Cliquez sur la carte pour ajouter des points (3 minimum). Faites glisser un point pour l'ajuster.
          </p>
        </div>
      )}

      <div className="rounded-2xl overflow-hidden border border-sand mb-6" style={{ height: 420 }} data-testid="zones-map">
        <MapContainer center={CONAKRY_CENTER} zoom={12} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {displayedZones.map((z) => (
            <Polygon
              key={z.zone_id}
              positions={z.polygon}
              pathOptions={{
                color: z.color || "#E32636",
                fillOpacity: z.active ? 0.22 : 0.06,
                weight: 2,
                dashArray: z.active ? null : "6 6",
              }}
            >
              <Tooltip sticky>{z.name}{z.active ? "" : " (désactivée)"}</Tooltip>
            </Polygon>
          ))}
          {draft && (
            <>
              <DrawClickHandler onAdd={(pt) => setDraft((d) => ({ ...d, points: [...d.points, pt] }))} />
              {draft.points.length >= 2 && (
                <Polygon positions={draft.points} pathOptions={{ color, fillOpacity: 0.25, weight: 2, dashArray: "4 4" }} />
              )}
              {draft.points.map((p, i) => (
                <Marker
                  key={i}
                  position={p}
                  icon={vertexIcon(color)}
                  draggable
                  eventHandlers={{
                    dragend: (e) => {
                      const ll = e.target.getLatLng();
                      moveVertex(i, +ll.lat.toFixed(6), +ll.lng.toFixed(6));
                    },
                  }}
                />
              ))}
            </>
          )}
        </MapContainer>
      </div>

      <div className="bg-white border border-sand rounded-2xl divide-y divide-sand" data-testid="zones-list">
        {loading && <div className="p-5 text-sm text-mute">Chargement…</div>}
        {!loading && zones.length === 0 && (
          <div className="p-5 text-sm text-mute">
            Aucune zone définie — la livraison est actuellement ouverte partout.
          </div>
        )}
        {zones.map((z) => (
          <div key={z.zone_id} className="flex items-center gap-3 px-4 py-3" data-testid={`zone-row-${z.zone_id}`}>
            <span className="w-4 h-4 rounded-full shrink-0 border border-white shadow" style={{ backgroundColor: z.color || "#E32636" }} />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm truncate">{z.name}</div>
              <div className="text-xs text-mute">{z.polygon.length} points · {z.active ? t("admin.zoneActive") : t("admin.zoneInactive")}</div>
            </div>
            <button onClick={() => toggleActive(z)} data-testid={`zone-toggle-${z.zone_id}`}
              title={z.active ? "Désactiver" : "Activer"}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${z.active ? "border-brand-green text-brand-green" : "border-sand text-mute"}`}>
              {z.active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {z.active ? "Active" : "Inactive"}
            </button>
            <button onClick={() => startEdit(z)} data-testid={`zone-edit-${z.zone_id}`} title="Modifier"
              className="p-2 rounded-full hover:bg-elevated text-mute hover:text-ink">
              <Pencil className="w-4 h-4" />
            </button>
            <button onClick={() => deleteZone(z)} data-testid={`zone-delete-${z.zone_id}`} title="Supprimer"
              className="p-2 rounded-full hover:bg-brand-red/10 text-mute hover:text-brand-red">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
